<template>
  <div v-if="modelValue" class="add-member-overlay" @click.self="close">
    <div class="add-member-modal" @click.stop>
      <div class="add-member-modal__header">
        <div class="add-member-modal__header-bg" aria-hidden="true" />
        <div class="add-member-modal__header-content">
          <div class="add-member-modal__header-icon">
            <svg v-if="step === 'email'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"/>
            </svg>
            <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
            </svg>
          </div>
          <div class="add-member-modal__header-text">
            <h3>{{ step === 'email' ? t('usuarios.addMember.modalTitle') : t('usuarios.addMember.registerTitle') }}</h3>
            <p>{{ step === 'email' ? t('usuarios.addMember.modalSubtitle') : t('usuarios.addMember.registerSubtitle') }}</p>
          </div>
          <button type="button" class="add-member-modal__close" :aria-label="t('common.close')" @click="close">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      </div>

      <div class="add-member-modal__steps" aria-hidden="true">
        <div class="add-member-step" :class="{ 'add-member-step--active': step === 'email', 'add-member-step--done': step === 'register' }">
          <span class="add-member-step__dot">1</span>
          <span class="add-member-step__label">{{ t('usuarios.addMember.stepEmail') }}</span>
        </div>
        <div class="add-member-step__line" :class="{ 'add-member-step__line--done': step === 'register' }" />
        <div class="add-member-step" :class="{ 'add-member-step--active': step === 'register' }">
          <span class="add-member-step__dot">2</span>
          <span class="add-member-step__label">{{ t('usuarios.addMember.stepRegister') }}</span>
        </div>
      </div>

      <div class="add-member-modal__body">
        <template v-if="step === 'email'">
          <div class="add-member-info">
            <svg class="add-member-info__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <p>{{ t('usuarios.addMember.emailHint') }}</p>
          </div>

          <div class="add-member-field">
            <label class="add-member-field__label">{{ t('usuarios.addMember.emailLabel') }}</label>
            <div class="add-member-field__wrap">
              <svg class="add-member-field__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
              <input
                v-model="email"
                type="email"
                class="add-member-field__input"
                :placeholder="t('usuarios.addMember.emailPlaceholder')"
                autocomplete="email"
                @keyup.enter="onContinue"
              />
            </div>
          </div>
        </template>

        <template v-else>
          <div class="add-member-email-badge">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
            <span>{{ emailTrimmed }}</span>
          </div>

          <div class="add-member-field">
            <label class="add-member-field__label">{{ t('usuarios.addMember.nameLabel') }}</label>
            <div class="add-member-field__wrap">
              <svg class="add-member-field__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg>
              <input
                v-model="nomeCompleto"
                type="text"
                class="add-member-field__input"
                :placeholder="t('usuarios.addMember.namePlaceholder')"
                autocomplete="name"
              />
            </div>
          </div>

          <div class="add-member-field">
            <label class="add-member-field__label">{{ t('usuarios.addMember.passwordLabel') }}</label>
            <div class="add-member-field__wrap">
              <svg class="add-member-field__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
              </svg>
              <input
                v-model="senha"
                :type="showPassword ? 'text' : 'password'"
                class="add-member-field__input add-member-field__input--password"
                :placeholder="t('usuarios.addMember.passwordPlaceholder')"
                autocomplete="new-password"
                @keyup.enter="onCreateAndAdd"
              />
              <button
                type="button"
                class="add-member-password-toggle"
                :title="showPassword ? t('usuarios.addMember.hidePassword') : t('usuarios.addMember.showPassword')"
                @click="showPassword = !showPassword"
              >
                <svg v-if="!showPassword" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
                <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                </svg>
              </button>
            </div>
          </div>
        </template>
      </div>

      <div class="add-member-modal__footer">
        <button type="button" class="add-member-btn add-member-btn--ghost" :disabled="busy" @click="close">
          {{ t('common.cancel') }}
        </button>
        <button
          v-if="step === 'email'"
          type="button"
          class="add-member-btn add-member-btn--primary"
          :disabled="busy || !emailTrimmed"
          @click="onContinue"
        >
          <span v-if="busy" class="add-member-btn__spinner" />
          {{ busy ? t('common.loading') : t('usuarios.addMember.continue') }}
        </button>
        <button
          v-else
          type="button"
          class="add-member-btn add-member-btn--primary"
          :disabled="busy || !canSubmitRegister"
          @click="onCreateAndAdd"
        >
          <span v-if="busy" class="add-member-btn__spinner" />
          {{ busy ? t('common.saving') : t('usuarios.addMember.createAndAdd') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'
import api from '@/services/api'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  workspaceId: { type: [Number, String], required: true }
})

const emit = defineEmits(['update:modelValue', 'added'])

const { t } = useI18n()
const toast = useToast()

const step = ref('email')
const email = ref('')
const nomeCompleto = ref('')
const senha = ref('')
const showPassword = ref(false)
const verifying = ref(false)
const submitting = ref(false)

const emailTrimmed = computed(() => email.value.trim())
const busy = computed(() => verifying.value || submitting.value)
const canSubmitRegister = computed(() => {
  return emailTrimmed.value && nomeCompleto.value.trim().length >= 2 && senha.value.length >= 6
})

function resetForm () {
  step.value = 'email'
  email.value = ''
  nomeCompleto.value = ''
  senha.value = ''
  showPassword.value = false
  verifying.value = false
  submitting.value = false
}

function close (force = false) {
  if (!force && busy.value) return
  emit('update:modelValue', false)
  resetForm()
}

function parseApiError (error, fallback) {
  return error?.response?.data?.error || fallback
}

async function addExistingMember () {
  submitting.value = true
  try {
    await api.post(`/workspace-acesso/workspace/${props.workspaceId}/adicionar-membro`, {
      email: emailTrimmed.value,
      roleWorkspace: 'MEMBER'
    })
    toast.success(t('usuarios.addMember.addedExisting'))
    emit('added')
    close(true)
  } catch (error) {
    const status = error?.response?.status
    if (status === 409) {
      toast.error(t('usuarios.addMember.alreadyMember'))
    } else if (status === 403) {
      toast.error(t('usuarios.addMember.forbidden'))
    } else {
      toast.error(parseApiError(error, t('usuarios.addMember.addError')))
    }
  } finally {
    submitting.value = false
  }
}

async function onContinue () {
  if (!emailTrimmed.value || busy.value) return
  verifying.value = true
  try {
    const { data } = await api.get(
      `/workspace-acesso/workspace/${props.workspaceId}/verificar-email`,
      { params: { email: emailTrimmed.value } }
    )
    if (data?.exists) {
      await addExistingMember()
    } else {
      step.value = 'register'
    }
  } catch (error) {
    toast.error(parseApiError(error, t('usuarios.addMember.verifyError')))
  } finally {
    verifying.value = false
  }
}

async function onCreateAndAdd () {
  if (!canSubmitRegister.value || busy.value) return
  submitting.value = true
  try {
    await api.post(`/workspace-acesso/workspace/${props.workspaceId}/adicionar-membro`, {
      email: emailTrimmed.value,
      nomeCompleto: nomeCompleto.value.trim(),
      senha: senha.value,
      roleWorkspace: 'MEMBER'
    })
    toast.success(t('usuarios.addMember.addedNew'))
    emit('added')
    close(true)
  } catch (error) {
    const status = error?.response?.status
    if (status === 409) {
      toast.error(t('usuarios.addMember.alreadyMember'))
    } else if (status === 403) {
      toast.error(t('usuarios.addMember.forbidden'))
    } else {
      toast.error(parseApiError(error, t('usuarios.addMember.addError')))
    }
  } finally {
    submitting.value = false
  }
}

watch(() => props.modelValue, (open) => {
  if (!open) resetForm()
})
</script>

<style scoped>
.add-member-overlay {
  position: fixed;
  inset: 0;
  z-index: 10060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(6px);
}

.add-member-modal {
  width: min(500px, 100%);
  background: var(--bg-secondary, #fff);
  border: 1px solid var(--border-primary, #e5e7eb);
  border-radius: 4px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.28);
  overflow: hidden;
}

.add-member-modal__header {
  position: relative;
  overflow: hidden;
}

.add-member-modal__header-bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
}

.add-member-modal__header-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 20px 20px 18px;
}

.add-member-modal__header-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.28);
  color: #fff;
}

.add-member-modal__header-text {
  flex: 1;
  min-width: 0;
  padding-top: 2px;
}

.add-member-modal__header-text h3 {
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.02em;
}

.add-member-modal__header-text p {
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.85);
}

.add-member-modal__close {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  cursor: pointer;
  transition: background 0.15s ease;
}

.add-member-modal__close:hover {
  background: rgba(255, 255, 255, 0.22);
}

.add-member-modal__steps {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 20px;
  background: var(--bg-primary, #f9fafb);
  border-bottom: 1px solid var(--border-primary, #e5e7eb);
}

.add-member-step {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary, #9ca3af);
  transition: color 0.2s ease;
}

.add-member-step--active {
  color: #6366f1;
}

.add-member-step--done {
  color: #6366f1;
}

.add-member-step__dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  background: var(--border-primary, #e5e7eb);
  color: var(--text-secondary, #6b7280);
  transition: all 0.2s ease;
}

.add-member-step--active .add-member-step__dot,
.add-member-step--done .add-member-step__dot {
  background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%);
  color: #fff;
}

.add-member-step__label {
  font-size: 13px;
  font-weight: 600;
}

.add-member-step__line {
  width: 48px;
  height: 2px;
  border-radius: 1px;
  background: var(--border-primary, #e5e7eb);
  transition: background 0.2s ease;
}

.add-member-step__line--done {
  background: linear-gradient(90deg, #6366f1, #7c3aed);
}

.add-member-modal__body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.add-member-info {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 4px;
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.18);
}

.add-member-info__icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  color: #6366f1;
}

.add-member-info p {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-secondary, #4b5563);
}

.add-member-email-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #4f46e5;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.22);
}

.add-member-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.add-member-field__label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary, #111827);
}

.add-member-field__wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.add-member-field__icon {
  position: absolute;
  left: 12px;
  width: 18px;
  height: 18px;
  color: var(--text-secondary, #9ca3af);
  pointer-events: none;
}

.add-member-field__input {
  width: 100%;
  padding: 11px 12px 11px 40px;
  border: 1px solid var(--border-primary, #e5e7eb);
  border-radius: 4px;
  font-size: 14px;
  background: var(--bg-primary, #fff);
  color: var(--text-primary, #111827);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.add-member-field__input--password {
  padding-right: 44px;
}

.add-member-field__input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
}

.add-member-password-toggle {
  position: absolute;
  right: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--text-secondary, #6b7280);
  cursor: pointer;
}

.add-member-password-toggle:hover {
  background: rgba(0, 0, 0, 0.05);
  color: var(--text-primary, #111827);
}

.add-member-modal__footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 14px 20px 18px;
  border-top: 1px solid var(--border-primary, #e5e7eb);
  background: var(--bg-primary, #fafafa);
}

.add-member-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 18px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: opacity 0.15s ease, transform 0.1s ease;
}

.add-member-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.add-member-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.add-member-btn--ghost {
  background: var(--bg-secondary, #fff);
  border-color: var(--border-primary, #e5e7eb);
  color: var(--text-primary, #111827);
}

.add-member-btn--ghost:hover:not(:disabled) {
  background: var(--bg-hover, #f3f4f6);
}

.add-member-btn--primary {
  background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%);
  color: #fff;
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.35);
}

.add-member-btn--primary:hover:not(:disabled) {
  box-shadow: 0 6px 18px rgba(99, 102, 241, 0.45);
}

.add-member-btn__spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: add-member-spin 0.7s linear infinite;
}

@keyframes add-member-spin {
  to { transform: rotate(360deg); }
}
</style>
